/**
 * Group by selector for stats table.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { ChevronDown, ChevronRight, Layers } from 'lucide-react';

import { Button } from '@/components/ui';

import { GROUP_BY_OPTIONS } from '../../shared/constants';
import type { GroupByKey } from '../../shared/constants';
import type { StatsGroupGeneric } from '../../hooks';

interface GroupByOption {
  key: string;
  label: string;
}

interface GroupBySelectProps {
  value: GroupByKey;
  onChange: (value: GroupByKey) => void;
  groups: StatsGroupGeneric[] | null;
  collapsedGroups: Set<string>;
  onCollapseAll: () => void;
  onExpandAll: () => void;
  groupByOptions?: readonly GroupByOption[];
}

export const GroupBySelect: FC<GroupBySelectProps> = ({
  value,
  onChange,
  groups,
  groupByOptions: groupByOptionsProp,
  collapsedGroups,
  onCollapseAll,
  onExpandAll,
}) => {
  const groupByOptions = groupByOptionsProp ?? GROUP_BY_OPTIONS;
  const hasGroups = groups != null && groups.length > 0;
  const allCollapsed =
    hasGroups && groups.every((g) => collapsedGroups.has(g.key));

  return (
    <div className="flex items-center gap-2">
      <Layers className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as GroupByKey)}
        className="h-9 rounded-md border border-input bg-background px-2.5 text-sm text-foreground"
        aria-label="Group by"
      >
        {groupByOptions.map((opt) => (
          <option key={opt.key} value={opt.key}>
            {opt.label}
          </option>
        ))}
      </select>
      {value !== 'none' && hasGroups && (
        <Button
          variant="ghost"
          size="sm"
          className="h-9 gap-1.5 px-2 text-muted-foreground hover:text-foreground"
          onClick={allCollapsed ? onExpandAll : onCollapseAll}
          aria-label={allCollapsed ? 'Expand all groups' : 'Collapse all groups'}
        >
          {allCollapsed ? (
            <>
              <ChevronRight className="h-4 w-4" />
              Expand all
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4" />
              Collapse all
            </>
          )}
        </Button>
      )}
    </div>
  );
};
